import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { learningPlanApi } from '@/services/learningManagementApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2, BookMarked, Target, AlertCircle, Trophy, Calendar, Clock, ExternalLink, CheckCircle } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { LearningPlan } from '@/types/learningManagement';

export const LearningPlanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('edit', 'learningPlan');
  const canDelete = hasPermission('delete', 'learningPlan');

  // Use entity hook for fetching learning plan
  const {
    data: learningPlan,
    isLoading,
    error,
    refetch,
  } = useEntity<LearningPlan>({
    fetchFn: (planId: string) => learningPlanApi.getById(planId),
    entityId: id,
    entityName: 'learningPlan',
    enabled: !!id,
  });

  const generationSource = learningPlan?.learningPlanGenerated || learningPlan?.learningPathGenerated;
  const isSystemDefined = generationSource === 'SYSTEM_DEFINED';
  const canModify = canEdit && (!isSystemDefined || hasPermission('system_admin', 'learningPlan'));

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Learning Plan ID is required');
      await learningPlanApi.delete(id);
    },
    entityName: 'learningPlan',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/learning-plans';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this learning plan?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading learning plan..." />;
  }

  if (error || !learningPlan) {
    return <ErrorState error={error || new Error('Learning plan not found')} onRetry={refetch} />;
  }

  const planId = learningPlan.id || '';
  const planType = learningPlan.planType || learningPlan.pathType;
  const planPlan = learningPlan.learningPlan || learningPlan.pathPlan;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'COMPLETED': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'BEGINNER': case 'EASY': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'INTERMEDIATE': case 'MODERATE': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ADVANCED': case 'CHALLENGING': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'EXPERT': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/learning-plans">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <BookMarked className="h-6 w-6 text-emerald-600" />
              <h1 className="text-3xl font-bold tracking-tight">{learningPlan.title}</h1>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(learningPlan.status)}`}>
                {learningPlan.status}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(learningPlan.difficultyLevel)}`}>
                {learningPlan.difficultyLevel}
              </span>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {(planType || 'N/A').replace(/_/g, ' ')}
              </span>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                {(planPlan || 'N/A').replace(/_/g, ' ')}
              </span>
              {isSystemDefined && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                  System Defined
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canModify && (
            <Button asChild>
              <Link to={`/admin/learning-plans/${planId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && !isSystemDefined && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Core learning plan details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={planId} label="Learning Plan ID" />
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1 text-sm bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">{learningPlan.description}</p>
            </div>
            {learningPlan.estimatedDurationHours && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Estimated Duration</label>
                <p className="mt-1 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{learningPlan.estimatedDurationHours} hours</span>
                </p>
              </div>
            )}
            {generationSource && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Generated</label>
                <p className="mt-1 text-sm">{generationSource.replace(/_/g, ' ')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audit Information */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
            <CardDescription>Creation and update history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="mt-1 text-sm">
                  {learningPlan.createdAt ? new Date(learningPlan.createdAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                <p className="mt-1 text-sm">
                  {learningPlan.updatedAt ? new Date(learningPlan.updatedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              {learningPlan.createdBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created By</label>
                  <p className="mt-1 text-sm">{learningPlan.createdBy}</p>
                </div>
              )}
              {learningPlan.updatedBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                  <p className="mt-1 text-sm">{learningPlan.updatedBy}</p>
                </div>
              )}
              {learningPlan.version !== undefined && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Version</label>
                  <p className="mt-1 text-sm">{learningPlan.version}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Related Win */}
      {learningPlan.win && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Related Win
            </CardTitle>
            <CardDescription>Achievement/milestone associated with this learning plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Link
                  to={`/admin/wins/${learningPlan.win.winId}`}
                  className="font-medium text-blue-600 hover:underline flex items-center gap-2"
                >
                  {learningPlan.win.winId}
                  <ExternalLink className="h-4 w-4" />
                </Link>
              </div>
              {learningPlan.win.winDescription && (
                <p className="text-sm text-muted-foreground">{learningPlan.win.winDescription}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Skill Sets */}
      {learningPlan.addressingSkillSets && learningPlan.addressingSkillSets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-500" />
              Addressing Skill Sets ({learningPlan.addressingSkillSets.length})
            </CardTitle>
            <CardDescription>Skills addressed by this learning plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {learningPlan.addressingSkillSets.map((skillSet, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/admin/skills/${skillSet.skillId}`}
                      className="font-medium text-blue-600 hover:underline flex items-center gap-2"
                    >
                      {skillSet.skillName || skillSet.skillId}
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-xs">
                      {skillSet.proficiencyLevel}
                    </span>
                  </div>
                  <CopyableId id={skillSet.skillId} label="Skill ID" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pain Points */}
      {learningPlan.painPoints && learningPlan.painPoints.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Pain Points ({learningPlan.painPoints.length})
            </CardTitle>
            <CardDescription>Pain points addressed by this learning plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {learningPlan.painPoints.map((painPoint, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/pain-points/${painPoint.painPointId}`}
                      className="font-medium text-blue-600 hover:underline flex items-center gap-2"
                    >
                      View Pain Point
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </div>
                  <p className="text-sm">{painPoint.painPointDescription}</p>
                  <p className="text-xs text-muted-foreground font-mono">{painPoint.painPointId}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prerequisites */}
      {learningPlan.prerequisites && learningPlan.prerequisites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Prerequisites ({learningPlan.prerequisites.length})</CardTitle>
            <CardDescription>Requirements before starting this learning plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {learningPlan.prerequisites.map((prereq, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-sm"
                >
                  {prereq}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Learning Modules */}
      {learningPlan.learningModules && learningPlan.learningModules.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-500" />
              Learning Modules ({learningPlan.learningModules.length})
            </CardTitle>
            <CardDescription>Structured learning content and daily activities</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {learningPlan.learningModules.map((module, moduleIdx) => (
              <div key={moduleIdx} className="border rounded-lg p-4 space-y-4">
                {/* Module Header */}
                <div className="flex items-center justify-between pb-2 border-b">
                  <h4 className="font-semibold text-lg">Module {moduleIdx + 1}</h4>
                  <div className="flex items-center gap-2">
                    {(module as { order?: number }).order !== undefined && (
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs">
                        Order: {(module as { order?: number }).order}
                      </span>
                    )}
                    {module.totalDays !== undefined && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                        {module.totalDays} days
                      </span>
                    )}
                  </div>
                </div>

                {/* Final Assessment */}
                {module.finalAssessmentId && (
                  <div className="flex items-center gap-2 p-2 bg-indigo-50 dark:bg-indigo-950/30 rounded">
                    <CheckCircle className="h-4 w-4 text-indigo-600" />
                    <span className="text-sm font-medium">Final Assessment:</span>
                    <Link
                      to={`/admin/assessments/${module.finalAssessmentId}`}
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {module.finalAssessmentId}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                )}

                {/* Final Reflection Prompts */}
                {module.finalReflectionPrompts && module.finalReflectionPrompts.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Final Reflection Prompts</label>
                    <div className="mt-2 space-y-2">
                      {module.finalReflectionPrompts.map((prompt: string, promptIdx: number) => (
                        <div key={promptIdx} className="flex items-start gap-2 p-2 bg-muted/50 rounded">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 flex items-center justify-center text-xs">
                            {promptIdx + 1}
                          </span>
                          <span className="text-sm">{prompt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Daily Contents */}
                {module.dailyContents && module.dailyContents.length > 0 && (
                  <div>
                    <label className="text-sm font-medium">Daily Contents ({module.dailyContents.length})</label>
                    <div className="mt-2 space-y-3">
                      {module.dailyContents.map((content, contentIdx) => (
                        <div key={contentIdx} className="border rounded-lg p-4 bg-gray-50/50 dark:bg-gray-900/30 space-y-3">
                          {/* Day Header */}
                          <div className="flex items-center justify-between pb-2 border-b">
                            <h5 className="font-medium">
                              Day {content.dayNumber !== undefined ? content.dayNumber : contentIdx + 1}
                              {content.title && `: ${content.title}`}
                            </h5>
                            <div className="flex items-center gap-2">
                              {(content as { status?: string }).status && (
                                <span className={`px-2 py-1 rounded text-xs ${
                                  (content as { status?: string }).status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                  (content as { status?: string }).status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {(content as { status?: string }).status}
                                </span>
                              )}
                              {content.isMilestone && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded text-xs">
                                  Milestone
                                </span>
                              )}
                              {content.estimatedDurationMinutes !== undefined && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs font-medium">
                                  {content.estimatedDurationMinutes} min
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Description */}
                          {content.description && (
                            <p className="text-sm text-muted-foreground">{content.description}</p>
                          )}

                          {/* Learning Objectives */}
                          {content.learningObjectives && content.learningObjectives.length > 0 && (
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Learning Objectives</label>
                              <ul className="mt-1 space-y-1">
                                {content.learningObjectives.map((obj: string, objIdx: number) => (
                                  <li key={objIdx} className="flex items-start gap-2 text-sm">
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    {obj}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Content IDs Grid */}
                          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 pt-2 border-t">
                            {/* Lessons */}
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Lessons ({content.lessonIds?.length || 0})</label>
                              <div className="mt-1 space-y-1">
                                {content.lessonIds && content.lessonIds.length > 0 ? content.lessonIds.map((lessonId: string, lessonIdx: number) => (
                                  <Link
                                    key={lessonIdx}
                                    to={`/admin/lessons/${lessonId}`}
                                    className="block px-2 py-1 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 hover:bg-blue-100 rounded text-xs font-mono truncate"
                                  >
                                    {lessonId}
                                  </Link>
                                )) : <span className="text-xs text-muted-foreground italic">None</span>}
                              </div>
                            </div>

                            {/* Nudges */}
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Nudges ({content.nudgeIds?.length || 0})</label>
                              <div className="mt-1 space-y-1">
                                {content.nudgeIds && content.nudgeIds.length > 0 ? content.nudgeIds.map((nudgeId: string, nudgeIdx: number) => (
                                  <Link
                                    key={nudgeIdx}
                                    to={`/admin/nudges/${nudgeId}`}
                                    className="block px-2 py-1 bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 hover:bg-purple-100 rounded text-xs font-mono truncate"
                                  >
                                    {nudgeId}
                                  </Link>
                                )) : <span className="text-xs text-muted-foreground italic">None</span>}
                              </div>
                            </div>

                            {/* Tips */}
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Tips ({content.tipIds?.length || 0})</label>
                              <div className="mt-1 space-y-1">
                                {content.tipIds && content.tipIds.length > 0 ? content.tipIds.map((tipId: string, tipIdx: number) => (
                                  <Link
                                    key={tipIdx}
                                    to={`/admin/tips/${tipId}`}
                                    className="block px-2 py-1 bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-100 rounded text-xs font-mono truncate"
                                  >
                                    {tipId}
                                  </Link>
                                )) : <span className="text-xs text-muted-foreground italic">None</span>}
                              </div>
                            </div>

                            {/* Tidbits */}
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Tidbits ({content.tidbitIds?.length || 0})</label>
                              <div className="mt-1 space-y-1">
                                {content.tidbitIds && content.tidbitIds.length > 0 ? content.tidbitIds.map((tidbitId: string, tidbitIdx: number) => (
                                  <Link
                                    key={tidbitIdx}
                                    to={`/admin/tidbits/${tidbitId}`}
                                    className="block px-2 py-1 bg-orange-50 dark:bg-orange-950 text-orange-700 dark:text-orange-300 hover:bg-orange-100 rounded text-xs font-mono truncate"
                                  >
                                    {tidbitId}
                                  </Link>
                                )) : <span className="text-xs text-muted-foreground italic">None</span>}
                              </div>
                            </div>

                            {/* Hacks */}
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Hacks ({content.hackIds?.length || 0})</label>
                              <div className="mt-1 space-y-1">
                                {content.hackIds && content.hackIds.length > 0 ? content.hackIds.map((hackId: string, hackIdx: number) => (
                                  <Link
                                    key={hackIdx}
                                    to={`/admin/hacks/${hackId}`}
                                    className="block px-2 py-1 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 hover:bg-red-100 rounded text-xs font-mono truncate"
                                  >
                                    {hackId}
                                  </Link>
                                )) : <span className="text-xs text-muted-foreground italic">None</span>}
                              </div>
                            </div>

                            {/* Nanos */}
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Nanos ({content.nanoIds?.length || 0})</label>
                              <div className="mt-1 space-y-1">
                                {content.nanoIds && content.nanoIds.length > 0 ? content.nanoIds.map((nanoId: string, nanoIdx: number) => (
                                  <Link
                                    key={nanoIdx}
                                    to={`/admin/nanos/${nanoId}`}
                                    className="block px-2 py-1 bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-300 hover:bg-teal-100 rounded text-xs font-mono truncate"
                                  >
                                    {nanoId}
                                  </Link>
                                )) : <span className="text-xs text-muted-foreground italic">None</span>}
                              </div>
                            </div>

                            {/* Assessment */}
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Assessment</label>
                              <div className="mt-1">
                                {content.assessmentId ? (
                                  <Link
                                    to={`/admin/assessments/${content.assessmentId}`}
                                    className="block px-2 py-1 bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 rounded text-xs font-mono truncate"
                                  >
                                    {content.assessmentId}
                                  </Link>
                                ) : <span className="text-xs text-muted-foreground italic">None</span>}
                              </div>
                            </div>

                            {/* Reflection Prompts (API schema) */}
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">
                                Reflection Prompts ({content.reflectionPrompts?.length || 0})
                              </label>
                              <div className="mt-1 space-y-1">
                                {content.reflectionPrompts && content.reflectionPrompts.length > 0 ? (
                                  content.reflectionPrompts.map((prompt: string, promptIdx: number) => (
                                    <div
                                      key={promptIdx}
                                      className="px-2 py-1 bg-pink-50 dark:bg-pink-950 text-pink-800 dark:text-pink-200 rounded text-xs"
                                    >
                                      {prompt}
                                    </div>
                                  ))
                                ) : (
                                  <span className="text-xs text-muted-foreground italic">None</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Tags */}
                          {content.tags && content.tags.length > 0 && (
                            <div className="pt-2 border-t">
                              <div className="flex flex-wrap gap-1">
                                {content.tags.map((tag: string, tagIdx: number) => (
                                  <span key={tagIdx} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      {learningPlan.tags && learningPlan.tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tags ({learningPlan.tags.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {learningPlan.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LearningPlanDetailPage;
